import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { clearToken, get, getToken, post, setToken } from '../api';

type User = {
    id: number;
    username: string;
    created_at: string;
    updated_at: string;
};

type AuthContextType = {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (token: string, user: User) => void;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const token = getToken();
        if (!token) {
            setIsLoading(false);
            return;
        }

        get('/auth/me')
            .then(async (response) => {
                if (!response.ok) {
                    clearToken();
                    return;
                }
                setUser(await response.json());
            })
            .finally(() => setIsLoading(false));
    }, []);

    const login = useCallback((token: string, nextUser: User) => {
        setToken(token);
        setUser(nextUser);
    }, []);

    const logout = useCallback(async () => {
        await post('/auth/logout').catch(() => undefined);
        clearToken();
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: user !== null, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
